import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  UserPlus, FileText, ClipboardCheck, Rocket, 
  Calendar, Clock, Star, CheckCircle, ArrowRight,
  Building2, MapPin, Camera, CreditCard, HelpCircle
} from 'lucide-react';

const StepCard = ({ number, icon: Icon, title, description, tips, color }) => (
  <div className="relative flex gap-6 group" data-testid={`step-${number}`}>
    <div className="flex flex-col items-center">
      <div className={`w-12 h-12 rounded-full ${color} text-white flex items-center justify-center text-lg font-bold flex-shrink-0 shadow-lg`}>
        {number}
      </div>
      {number < 4 && <div className="w-0.5 h-full bg-neutral-200 mt-2" />}
    </div>
    <div className="pb-12 flex-1">
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-neutral-100 hover:shadow-md transition-shadow">
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-10 h-10 rounded-xl ${color}/10 flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${color === 'bg-primary' ? 'text-primary' : color === 'bg-accent' ? 'text-accent' : color === 'bg-emerald-500' ? 'text-emerald-500' : 'text-violet-500'}`} />
          </div>
          <h3 className="text-lg font-bold text-neutral-900">{title}</h3>
        </div>
        <p className="text-neutral-600 leading-relaxed mb-4">{description}</p>
        {tips && (
          <div className="bg-neutral-50 rounded-xl p-4 space-y-2">
            {tips.map((tip, i) => (
              <div key={i} className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-neutral-600">{tip}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  </div>
);

const HowTo = () => {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
    }
  }, [hash]);

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="how-to-page">
      <Helmet>
        <title>How to Add Your Business or Event | Explore MHC - Martinsville & Henry County</title>
        <meta name="description" content="Step-by-step guide to adding your business or event to Explore MHC. Free listing for all businesses in Martinsville & Henry County, VA. Get approved in 48 hours." />
        <meta name="keywords" content="add business Martinsville, submit event Henry County, business directory Virginia, community events MHC, list my business" />
        <link rel="canonical" href="https://exploremhc.com/how-to" />
        <meta property="og:title" content="How to Add Your Business or Event | Explore MHC" />
        <meta property="og:description" content="Step-by-step guide to listing your business or event on Explore MHC. Free for all businesses. Approved within 48 hours." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://exploremhc.com/how-to" />
        <meta property="og:image" content="https://images.unsplash.com/photo-1622127739239-1905bbaa21b8?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200&h=630&fit=crop" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          "name": "How to Add Your Business or Event to Explore MHC",
          "description": "A step-by-step guide to listing your business or submitting an event on the Explore MHC community platform for Martinsville and Henry County, Virginia.",
          "step": [
            { "@type": "HowToStep", "position": 1, "name": "Create Your Account", "text": "Register as a Business Owner or Community Member at exploremhc.com/register" },
            { "@type": "HowToStep", "position": 2, "name": "Fill Out Your Listing", "text": "Complete the business submission form with your details, photos, and hours of operation" },
            { "@type": "HowToStep", "position": 3, "name": "Submit for Review", "text": "Submit your listing for admin review. All submissions are reviewed within 48 hours." },
            { "@type": "HowToStep", "position": 4, "name": "Go Live", "text": "Once approved, your listing appears on the directory and is visible to the community." }
          ],
          "totalTime": "PT10M"
        })}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-primary to-primary-dark text-white py-16 sm:py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-40 h-40 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-20 w-56 h-56 bg-accent rounded-full blur-3xl" />
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <HelpCircle className="w-4 h-4" />
              <span className="text-sm font-medium">Step-by-Step Guide</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" style={{ fontFamily: 'DM Serif Display, serif' }}>
              Get Listed on Explore MHC
            </h1>
            <p className="text-lg sm:text-xl text-white/80 max-w-2xl mx-auto mb-8">
              Adding your business or event is free and takes less than 10 minutes. Here's everything you need to know.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-xl px-5 py-3">
                <Clock className="w-5 h-5 text-accent" />
                <span className="font-medium">48-Hour Approval</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-xl px-5 py-3">
                <CreditCard className="w-5 h-5 text-accent" />
                <span className="font-medium">Free to List</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-xl px-5 py-3">
                <Star className="w-5 h-5 text-accent" />
                <span className="font-medium">Featured Options Available</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How to Add Your Business */}
      <section className="py-16 sm:py-20" id="add-business">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-2">
              <Building2 className="w-6 h-6 text-primary" />
              <span className="text-sm font-semibold text-primary uppercase tracking-wider">For Business Owners</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900 mb-4" style={{ fontFamily: 'DM Serif Display, serif' }}>
              How to Add Your Business
            </h2>
            <p className="text-neutral-600 mb-10 max-w-2xl">
              Join the growing Martinsville & Henry County business directory. Listing your business is completely free and helps locals discover what you offer.
            </p>

            {/* Tutorial video for business section */}
            <div className="mb-12 rounded-2xl overflow-hidden shadow-lg bg-black">
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src="https://www.youtube.com/embed/42y1ERXrwbE?si=jXRiR9xrLbX1VZEk"
                  title="How to Add Your Business to Explore MHC"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  data-testid="howto-business-video"
                />
              </div>
            </div>

            {/* Steps */}
            <div className="pl-2">
              <StepCard
                number={1}
                icon={UserPlus}
                title="Create a Business Owner Account"
                color="bg-primary"
                description="Head to the registration page and select 'Business Owner' as your account type. Fill in your name, email, and create a password. You'll receive a verification email to confirm your account."
                tips={[
                  "Use the email you want to receive notifications on",
                  "Check your spam folder if you don't see the verification email",
                  "Each business owner account can manage one business listing"
                ]}
              />

              <StepCard
                number={2}
                icon={FileText}
                title="Fill Out Your Business Details"
                color="bg-accent"
                description="Navigate to 'Submit Business' from your dashboard. Complete the form with your business name, category, description (up to 300 characters), contact info, hours of operation, and upload up to 5 photos."
                tips={[
                  "Choose the best-fitting category and subcategory for search visibility",
                  "Write a clear, keyword-rich description of your services",
                  "Upload high-quality photos — your main photo appears on directory cards",
                  "Street address is optional for home-based businesses",
                  "Add your social media links (Facebook, Instagram, Twitter)"
                ]}
              />

              <StepCard
                number={3}
                icon={ClipboardCheck}
                title="Submit & Wait for Approval"
                color="bg-emerald-500"
                description="Once you submit, your listing enters the review queue. Our admin team reviews every submission to maintain quality. You'll receive an email notification when your listing is approved or if any changes are needed."
                tips={[
                  "Approval typically happens within 48 hours",
                  "If rejected, you'll see the reason on your dashboard and can edit & resubmit",
                  "Ensure your business is located in or serves Martinsville & Henry County"
                ]}
              />

              <StepCard
                number={4}
                icon={Rocket}
                title="You're Live — Get Discovered!"
                color="bg-violet-500"
                description="Once approved, your business appears in the directory and is searchable by the community. From your dashboard, you can edit your listing anytime, track page views, and upgrade to a featured placement for extra visibility."
                tips={[
                  "Featured placement starts at just $10/week",
                  "Featured businesses appear at the top of the directory and on the homepage",
                  "Share your listing link on social media for more exposure"
                ]}
              />
            </div>

            {/* CTA */}
            <div className="mt-4 bg-gradient-to-r from-primary to-primary-dark rounded-2xl p-8 text-white text-center">
              <h3 className="text-xl sm:text-2xl font-bold mb-3" style={{ fontFamily: 'DM Serif Display, serif' }}>
                Ready to List Your Business?
              </h3>
              <p className="text-white/80 mb-6 max-w-lg mx-auto">
                It's free, fast, and puts you in front of the Martinsville & Henry County community.
              </p>
              <Link
                to="/register"
                className="inline-flex items-center gap-2 bg-accent hover:bg-accent/90 text-neutral-900 font-bold px-8 py-3 rounded-xl transition-colors"
                data-testid="cta-register-business"
              >
                Create Your Account <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto border-t border-neutral-200" />
      </div>

      {/* How to Submit an Event */}
      <section className="py-16 sm:py-20" id="submit-event">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-2">
              <Calendar className="w-6 h-6 text-accent" />
              <span className="text-sm font-semibold text-accent uppercase tracking-wider">For Everyone</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900 mb-4" style={{ fontFamily: 'DM Serif Display, serif' }}>
              How to Submit an Event
            </h2>
            <p className="text-neutral-600 mb-10 max-w-2xl">
              Whether you're a business owner or a community member, anyone with an account can submit local events for the community calendar.
            </p>

            {/* Hero image for events section */}
            <div className="mb-12 rounded-2xl overflow-hidden shadow-lg">
              <img 
                src="https://images.unsplash.com/photo-1686563183319-2089774d31cd?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200&h=400&fit=crop"
                alt="Plain calendar"
                className="w-full h-48 sm:h-64 object-cover"
                loading="lazy"
              />
            </div>

            {/* Steps */}
            <div className="pl-2">
              <StepCard
                number={1}
                icon={UserPlus}
                title="Log Into Your Account"
                color="bg-accent"
                description="Sign in with your Business Owner or Community Member account. Don't have one yet? Registration is free and takes just a minute."
                tips={[
                  "Both Business Owners and Community Members can submit events",
                  "If you only need to submit events, a Community Member account is perfect"
                ]}
              />

              <StepCard
                number={2}
                icon={FileText}
                title="Fill Out the Event Details"
                color="bg-primary"
                description="Go to 'Submit Event' from your dashboard or navigation menu. Add your event name, date (start and end), time, location, description, category, and an event image."
                tips={[
                  "Include the full address so attendees can find the venue easily",
                  "Add an end date for multi-day events",
                  "Upload an eye-catching event image or flyer",
                  "Write a description that covers: what, when, where, and who it's for"
                ]}
              />

              <StepCard
                number={3}
                icon={ClipboardCheck}
                title="Submit & Get Approved"
                color="bg-emerald-500"
                description="Your event enters the review queue and will be approved within 48 hours. You'll be notified by email when it goes live on the community events calendar."
                tips={[
                  "Events are automatically hidden from public view once the end date passes",
                  "You can view your past events on your account dashboard",
                  "Make sure event details are accurate — this builds community trust"
                ]}
              />

              <StepCard
                number={4}
                icon={Star}
                title="Boost with Featured Placement"
                color="bg-violet-500"
                description="Want your event front and center? Upgrade to a featured event placement. Featured events appear at the top of the events page and on the homepage, reaching more people in the community."
                tips={[
                  "Featured event pricing starts at just $5/week",
                  "Choose 'Until Event Date' to stay featured right up to your event",
                  "Featured events get a special badge and priority listing"
                ]}
              />
            </div>

            {/* CTA */}
            <div className="mt-4 bg-gradient-to-r from-accent to-amber-500 rounded-2xl p-8 text-neutral-900 text-center">
              <h3 className="text-xl sm:text-2xl font-bold mb-3" style={{ fontFamily: 'DM Serif Display, serif' }}>
                Have an Event to Share?
              </h3>
              <p className="text-neutral-800/80 mb-6 max-w-lg mx-auto">
                Let the Martinsville & Henry County community know what's happening.
              </p>
              <Link
                to="/submit-event"
                className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white font-bold px-8 py-3 rounded-xl transition-colors"
                data-testid="cta-submit-event"
              >
                Submit an Event <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ / Quick Info */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-center text-neutral-900 mb-12" style={{ fontFamily: 'DM Serif Display, serif' }}>
              Frequently Asked Questions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { q: "Is it free to list my business?", a: "Yes! Adding a standard business listing is completely free. Featured placements are optional paid upgrades starting at $10/week." },
                { q: "How long does approval take?", a: "Most submissions are reviewed and approved within 48 hours. You'll receive an email notification once your listing status changes." },
                { q: "What if my listing is rejected?", a: "You'll see the rejection reason on your dashboard. Simply click 'Edit & Resubmit' to make changes and resubmit for approval." },
                { q: "Can I edit my listing after it's live?", a: "Absolutely! You can update your business details, photos, hours, and contact info anytime from your dashboard." },
                { q: "Who can submit events?", a: "Both Business Owners and Community Members can submit events. Anyone with an account can share local happenings." },
                { q: "What happens to past events?", a: "Events are automatically hidden from public view after their end date passes, but they remain visible in your account dashboard." }
              ].map((faq, i) => (
                <div key={i} className="bg-neutral-50 rounded-2xl p-6" data-testid={`faq-${i}`}>
                  <h3 className="font-bold text-neutral-900 mb-2">{faq.q}</h3>
                  <p className="text-sm text-neutral-600 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-20 bg-neutral-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900 mb-4" style={{ fontFamily: 'DM Serif Display, serif' }}>
            Still Have Questions?
          </h2>
          <p className="text-neutral-600 mb-8 max-w-lg mx-auto">
            We're here to help. Reach out to our team and we'll get you set up.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white font-bold px-8 py-3 rounded-xl transition-colors"
            data-testid="cta-contact"
          >
            Contact Us <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HowTo;
